"use client";
import { useEffect, useMemo, useState } from "react";
import uniqBy from "lodash.uniqby";
import {
  useQueryImplementationsMeta,
  useQueryPrivateImplementationsMeta,
} from "@zapier/client";
import { Key, ServiceIcon } from "@zapier/design-system";
import { AssetTypes } from "@zapier/utils";
import { useTranslations } from "@zapier/i18n";

import { FilterTypeahead, TypeaheadItem } from "../FilterTypeahead";
import {
  ConnectionSearchInteractionOption,
  useEmitConnectionSearchInteractionEvent,
  useEmitZapSearchInteractionEvent,
  ZapSearchInteractionOptions,
} from "../../../events";
import { useRouterParams } from "../../../hooks";

import styles from "./AppFilter.module.css";

interface Props {
  assetType: AssetTypes;
}

interface TypeaheadAppItem extends TypeaheadItem {
  image?: string;
}

function AppFilter({ assetType }: Props) {
  const t = useTranslations("AppFilter");
  const { app, setParams } = useRouterParams();
  const { emit: emitZapSearchInteractionEvent } =
    useEmitZapSearchInteractionEvent();
  const { emit: emitConnectionSearchInteractionEvent } =
    useEmitConnectionSearchInteractionEvent();

  const [initialValue, setInitialValue] = useState<string>("");
  const [selectedKey, setSelectedKey] = useState<Key>("");
  const [searchValue, setSearchValue] = useState<string>("");

  // Fetch all loaded apps based on search value
  const {
    implementationsMeta: loadedApps,
    isLoading: isLoadingLoadedApps,
    isFetching: isFetchingLoadedApps,
  } = useQueryImplementationsMeta({
    term: searchValue,
  });

  // Fetch the initial app if there's one in the URL params (for page reload persistence)
  const {
    implementationsMeta: initialApps,
    isLoading: isLoadingInitialApps,
    isFetching: isFetchingInitialApps,
  } = useQueryPrivateImplementationsMeta({
    selectedApis: initialValue ? [initialValue] : [],
  });

  // Map of app IDs to their visibility for public app version stripping
  const appVisibilityMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const { id, visibility } of loadedApps) {
      if (visibility) {
        map.set(id, visibility);
      }
    }
    return map;
  }, [loadedApps]);

  const items = useMemo(() => {
    const mappedLoadedApps = loadedApps.map(({ id, name, image }) => ({
      id,
      name: name || "",
      image: image || undefined,
    }));

    const mappedInitialApps = initialApps.map(({ id, name, images }) => ({
      id: id || "",
      name: name || "",
      image: images?.url32X32 || undefined,
    }));

    const allApps = [...mappedInitialApps, ...mappedLoadedApps];

    // Filter out null ids and sort with public apps first
    const uniqueApps = uniqBy(allApps, "id").filter((app) => !!app?.id);

    return uniqueApps.sort((a, b) => {
      const aIsPublic = appVisibilityMap.get(a.id) === "public";
      const bIsPublic = appVisibilityMap.get(b.id) === "public";
      if (aIsPublic && !bIsPublic) return -1;
      if (!aIsPublic && bIsPublic) return 1;
      return 0;
    });
  }, [loadedApps, initialApps, appVisibilityMap]);

  // Strip version suffix from app ID for public apps to search all versions
  const getAppIdForSearch = (appId: string): string => {
    const visibility = appVisibilityMap.get(appId);
    if (visibility === "public") {
      return appId.replace(/@[\d.]+$/, "");
    }
    return appId;
  };

  // Check for an initial value on mount. This will happen if there
  // is an app in the URL query params
  useEffect(() => {
    const [implentationId] = (app || "").split("::");
    if (implentationId) {
      setInitialValue(implentationId);
    }
  }, []);

  // Sync selectedKey when app filter changes or when items load
  useEffect(() => {
    if (app) {
      // Find matching item - app might be stripped (public) so check for prefix match
      const matchingItem = items.find(
        (item) => item.id === app || item.id.startsWith(`${app}@`),
      );
      setSelectedKey(matchingItem?.id || app);
    }
  }, [app, items]);

  // Clear app filter fields when app filter is removed from the URL
  useEffect(() => {
    if (!app) {
      setSearchValue("");
      setSelectedKey("");
    }
  }, [app]);

  // Set the selected key when there is an initial value and the query
  // returns with some apps to populate the dropdown
  useEffect(() => {
    if (initialApps.length > 0 && initialValue) {
      // Use the full ID from the fetched app to ensure dropdown selection works
      setSelectedKey(initialApps[0].id || app || "");
    } else if (loadedApps && initialValue) {
      setSelectedKey(app || "");
    }
  }, [loadedApps, initialApps, initialValue, app]);

  // Callback fired when a key is selected from the dropdown
  const selectKey = (key: Key | null) => {
    if (assetType === AssetTypes.Zap) {
      emitZapSearchInteractionEvent({
        optionSelected: ZapSearchInteractionOptions.AppUsed,
      });
    }

    if (assetType === AssetTypes.Connection) {
      emitConnectionSearchInteractionEvent(
        ConnectionSearchInteractionOption.AppUsed,
      );
    }
    setSelectedKey(key || "");
    setSearchValue("");
    if (String(app) !== String(key)) {
      const keyString = String(key);
      setInitialValue(keyString);
      // Strip version from public apps to search all versions
      const appIdForSearch = key ? getAppIdForSearch(keyString) : "";
      setParams({ app: appIdForSearch });
    }
  };

  // Set the search value any time the input changes
  const searchChange = (search: string) => {
    setSearchValue(search);
  };

  return (
    <div>
      <FilterTypeahead
        label={t("label")}
        placeholder={t("placeholder")}
        items={items}
        selectedKey={selectedKey}
        onSelectKey={selectKey}
        onSearchChange={searchChange}
        isBusy={
          isLoadingLoadedApps ||
          isFetchingLoadedApps ||
          isLoadingInitialApps ||
          isFetchingInitialApps
        }
        renderItem={(item) => {
          return (
            <div className={styles.menuItem} key={item.id}>
              <ServiceIcon
                serviceName={item.name}
                size="small"
                src={(item as TypeaheadAppItem)?.image || ""}
              />
              <div>{item.name}</div>
            </div>
          );
        }}
      />
    </div>
  );
}

export default AppFilter;
